"use client";

import { HINT, INPUT_MONO, LABEL, SELECT } from "@/components/tool-kit/styles";

/** The kit's select without its `w-full`, which would fight the fixed unit-menu width. */
const UNIT_SELECT = SELECT.replace("w-full", "").trim();

/**
 * One physical quantity: a number box and its unit menu.
 *
 * When `calculated` is set the box becomes a read-only answer, so a calculator can solve
 * for any variable without the reader losing the other values they typed. All visible
 * text arrives through props.
 */
export interface UnitChoice {
  value: string;
  label: string;
}

export function QuantityField({
  id,
  label,
  value,
  onValue,
  unit,
  onUnit,
  units,
  unitLabel,
  hint,
  calculated = null,
  invalid = false,
}: {
  id: string;
  label: string;
  value: string;
  onValue: (value: string) => void;
  unit: string;
  onUnit: (unit: string) => void;
  units: readonly UnitChoice[];
  /** The unit menu's accessible name, e.g. "Unit for mass". */
  unitLabel: string;
  hint?: string;
  /** The solved value to show instead of an input. */
  calculated?: string | null;
  invalid?: boolean;
}) {
  const isAnswer = calculated !== null;
  return (
    <div className="space-y-2">
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      <div className="flex gap-2">
        <input
          aria-invalid={invalid ? true : undefined}
          className={`${INPUT_MONO} min-w-0 flex-1 ${isAnswer ? "bg-primary/5 font-semibold" : ""}`}
          id={id}
          inputMode="decimal"
          onChange={(event) => onValue(event.target.value)}
          readOnly={isAnswer}
          value={isAnswer ? calculated : value}
        />
        {units.length === 1 && units[0].label !== "" ? (
          <span className="flex shrink-0 items-center px-1 text-foreground text-sm">
            {units[0].label}
          </span>
        ) : null}
        {units.length > 1 ? (
          <select
            aria-label={unitLabel}
            className={`${UNIT_SELECT} w-28 shrink-0`}
            onChange={(event) => onUnit(event.target.value)}
            value={unit}
          >
            {units.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {hint ? <p className={HINT}>{hint}</p> : null}
    </div>
  );
}
